/**
 * The rebindable actions (08 §3), in the order the Controls screen lists them. Pure data: no DOM.
 *
 * A binding is a `KeyboardEvent.code`, a mouse button `Mouse0`..`Mouse4` (`MouseEvent.button`:
 * 0 left, 1 middle, 2 right) or a wheel notch `WheelUp` / `WheelDown`.
 */

export type Scheme = 'modern' | 'classic';

export const SCHEMES: readonly Scheme[] = ['modern', 'classic'];

/** Keys an action can hold at once. */
export const SLOTS = 2;

export const ACTIONS = [
  { id: 'moveUp', label: 'Move up' },
  { id: 'moveDown', label: 'Move down' },
  { id: 'moveLeft', label: 'Move left' },
  { id: 'moveRight', label: 'Move right' },
  { id: 'fire', label: 'Fire' },
  { id: 'use', label: 'Use item / throw' },
  { id: 'run', label: 'Run' },
  { id: 'crawl', label: 'Crawl' },
  { id: 'sneak', label: 'Sneak (crouched walk)' },
  { id: 'interact', label: 'Interact' },
  { id: 'reload', label: 'Reload' },
  { id: 'firstAid', label: 'First aid' },
  { id: 'recall', label: 'Recall Osprey (hold)' },
  { id: 'weapon1', label: 'Carbine' },
  { id: 'weapon2', label: 'Grenade' },
  { id: 'weapon3', label: 'LAW' },
  { id: 'weapon4', label: 'Time bomb' },
  { id: 'weapon5', label: 'Knife' },
  { id: 'pistol', label: 'Pistol' },
  { id: 'smoke', label: 'Smoke grenade' },
  { id: 'nextWeapon', label: 'Next weapon' },
  { id: 'prevWeapon', label: 'Previous weapon' },
  { id: 'quickKnife', label: 'Quick knife' },
  { id: 'fuse5', label: 'Bomb fuse 5 s' },
  { id: 'fuse10', label: 'Bomb fuse 10 s' },
  { id: 'fuse15', label: 'Bomb fuse 15 s' },
  { id: 'map', label: 'Map' },
  { id: 'pause', label: 'Pause' },
] as const;

export type ActionId = (typeof ACTIONS)[number]['id'];

const IDS: ReadonlySet<string> = new Set(ACTIONS.map((a) => a.id));

export function isActionId(id: string): id is ActionId {
  return IDS.has(id);
}

export function actionLabel(id: ActionId): string {
  return ACTIONS.find((a) => a.id === id)?.label ?? id;
}

/** Escape always pauses: it is the one key a player can rely on whatever he has done to the rest. */
export const PAUSE_KEY = 'Escape';

/** The contract the menus' Controls screen is written against. */
export interface BindingsPort {
  scheme(): Scheme;
  setScheme(s: Scheme): void;
  /** The ACTIVE scheme's bindings. `keys` holds only the bound slots, in slot order (0 to `SLOTS` entries). */
  actions(): ReadonlyArray<{ id: string; label: string; keys: readonly string[] }>;
  /**
   * Put `code` in a slot of an action of the active scheme; `''` clears the slot. Returns null on
   * success, else a message for the player (the key is taken, Esc must stay on Pause...).
   */
  rebind(actionId: string, slot: number, code: string): string | null;
  /** The active scheme back to its defaults. */
  reset(): void;
}
